import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { SanityLogo, SanityLogoSet } from '@/sanity/lib/navigation';

type NavbarLogoImagesProps = {
  logos?: SanityLogoSet | null;
  alt: string;
  className?: string;
  /** When true, prefer sticky logo assets for the solid navbar background. */
  onSolidBackground?: boolean;
};

function pickLogo(logo?: SanityLogo | null) {
  return logo?.src ? logo : null;
}

type LogoPair = {
  light: ReturnType<typeof pickLogo>;
  dark: ReturnType<typeof pickLogo>;
};

function resolveLogoPair({
  logos,
  onSolidBackground,
  viewport,
}: {
  logos?: SanityLogoSet | null;
  onSolidBackground: boolean;
  viewport: 'desktop' | 'mobile';
}): LogoPair {
  const useThemeLogos = logos?.enableThemeBasedLogoAlteration;
  const isMobile = viewport === 'mobile';

  const heroLight = pickLogo(
    isMobile
      ? useThemeLogos
        ? logos?.mobileLightLogo
        : logos?.mobileLogo
      : useThemeLogos
        ? logos?.lightLogo
        : logos?.logo,
  );
  const heroDark = pickLogo(
    isMobile
      ? useThemeLogos
        ? logos?.mobileDarkLogo
        : logos?.mobileLogo
      : useThemeLogos
        ? logos?.darkLogo
        : logos?.logo,
  );
  const stickyLight = pickLogo(isMobile ? logos?.mobileStickyLogo : logos?.stickyLogo);
  const stickyDark = pickLogo(isMobile ? logos?.mobileStickyDarkLogo : logos?.stickyDarkLogo);

  const desktopHeroLight = pickLogo(useThemeLogos ? logos?.lightLogo : logos?.logo);
  const desktopHeroDark = pickLogo(useThemeLogos ? logos?.darkLogo : logos?.logo);
  const desktopStickyLight = pickLogo(logos?.stickyLogo);
  const desktopStickyDark = pickLogo(logos?.stickyDarkLogo);

  const light = onSolidBackground
    ? (stickyLight ?? heroLight ?? desktopStickyLight ?? desktopHeroLight)
    : (heroLight ?? desktopHeroLight);
  const dark = onSolidBackground
    ? (stickyDark ??
      heroDark ??
      stickyLight ??
      desktopStickyDark ??
      desktopHeroDark ??
      desktopStickyLight)
    : (heroDark ?? desktopHeroDark);

  return { light, dark };
}

function ThemeLogoPair({
  alt,
  className,
  pair,
  wrapperClassName,
}: {
  alt: string;
  className?: string;
  pair: LogoPair;
  wrapperClassName?: string;
}) {
  const { light, dark } = pair;
  const imageClassName = cn('object-contain', className);

  if (light || dark) {
    const content = (
      <>
        {light ? (
          <Image
            src={light.src as string}
            alt={light.alt || alt}
            width={100}
            height={100}
            className={cn(imageClassName, dark && 'dark:hidden')}
          />
        ) : null}
        {dark ? (
          <Image
            src={dark.src as string}
            alt={dark.alt || alt}
            width={100}
            height={100}
            className={cn(imageClassName, light && 'hidden dark:block')}
          />
        ) : null}
      </>
    );

    if (wrapperClassName) {
      return <span className={wrapperClassName}>{content}</span>;
    }

    return content;
  }

  return null;
}

export function NavbarLogoImages({
  logos,
  alt,
  className,
  onSolidBackground = false,
}: NavbarLogoImagesProps) {
  const useThemeLogos = logos?.enableThemeBasedLogoAlteration;
  const narrowMobileClassName =
    'max-[427px]:max-h-8 max-[427px]:h-auto max-[427px]:w-auto max-[427px]:max-w-full';
  const mobileClassName = cn('h-8 w-auto', narrowMobileClassName);
  const desktopClassName = 'size-10';
  const responsiveClassName = cn('h-8 w-auto xl:size-10', narrowMobileClassName);
  const desktopPair = resolveLogoPair({ logos, onSolidBackground, viewport: 'desktop' });
  const mobilePair = resolveLogoPair({ logos, onSolidBackground, viewport: 'mobile' });
  const hasMobileSpecificLogo = Boolean(
    logos?.mobileLogo?.src ||
    logos?.mobileLightLogo?.src ||
    logos?.mobileDarkLogo?.src ||
    logos?.mobileStickyLogo?.src ||
    logos?.mobileStickyDarkLogo?.src,
  );

  if (
    useThemeLogos &&
    (desktopPair.light || desktopPair.dark || mobilePair.light || mobilePair.dark)
  ) {
    if (!hasMobileSpecificLogo) {
      return <ThemeLogoPair alt={alt} className={responsiveClassName} pair={desktopPair} />;
    }

    return (
      <>
        <ThemeLogoPair
          alt={alt}
          className={mobileClassName}
          pair={mobilePair}
          wrapperClassName="contents xl:hidden"
        />
        <ThemeLogoPair
          alt={alt}
          className={desktopClassName}
          pair={desktopPair}
          wrapperClassName="hidden xl:contents"
        />
      </>
    );
  }

  const mobileLogo = mobilePair.light ?? mobilePair.dark;
  const desktopLogo = desktopPair.light ?? desktopPair.dark ?? pickLogo(logos?.logo);

  if (hasMobileSpecificLogo && mobileLogo && desktopLogo) {
    return (
      <>
        <Image
          src={mobileLogo.src as string}
          alt={mobileLogo.alt || alt}
          width={100}
          height={100}
          className={cn('object-contain xl:hidden', mobileClassName, className)}
        />
        <Image
          src={desktopLogo.src as string}
          alt={desktopLogo.alt || alt}
          width={100}
          height={100}
          className={cn('hidden object-contain xl:block', desktopClassName, className)}
        />
      </>
    );
  }

  const singleLogo = mobileLogo ?? desktopLogo;
  if (!singleLogo?.src) return null;

  return (
    <Image
      src={singleLogo.src}
      alt={singleLogo.alt || alt}
      width={100}
      height={100}
      className={cn('object-contain', responsiveClassName, className)}
    />
  );
}
